import { useEffect, useEffectEvent, useMemo, useState } from "react";
import { Nav, Tab } from "react-bootstrap";
import { PlusCircle } from "react-bootstrap-icons";
import { AsyncButton } from "../../controls";
import { getFromDataPath } from "../../utility";
import { AnalysisProgrammingCodeTemplate, DocumentReference, Operation } from "../ars_ldm";
import { useARSContext } from "../contexts";
import { PATCH_ARS_STATE } from "../contexts/interface";
import { isAnalysisMethod } from "../typeGuards/isAnalysisMethod";
import { AnalysisProgrammingCodeTemplateControl } from "./AnalysisProgrammingCodeTemplateControl";
import { DocumentReferenceControl } from "./DocumentReferenceControl";
import { NamedObjectDisplay } from "./NamedObjectDisplay";
import { OperationControl } from "./OperationControl";
import { RemoveArsItemButton } from "./RemoveArsItemButton";

/**
 * Props for the AnalysisMethodControl component.
 */
export interface AnalysisMethodControlProps {
  /** RFC 6901 JSON Pointer path to the AnalysisMethod in ARSContext state. */
  dataPath: string;
  /** Can rename the object. */
  canRename?: boolean;
  /** Disabled state. */
  disabled?: boolean;
}

/**
 * A component for managing AnalysisMethod entities with nested operations.
 *
 * This component provides:
 * - Properties tab for editing named object fields
 * - Dynamic operation tabs for managing method operations
 * - Add operation action
 * - Optional remove button for deleting the analysis method
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the AnalysisMethod in ARSContext state.
 * @param props.canRename - Optional flag to allow renaming and removing the object.
 * @param props.disabled - Optional flag to disable editing controls.
 * @returns A React functional component rendering the analysis method editor.
 */
export const AnalysisMethodControl = ({
  dataPath,
  canRename = false,
  disabled = false,
}: AnalysisMethodControlProps): React.ReactElement => {
  const { state, dispatch } = useARSContext();
  const [activeTab, setActiveTab] = useState<string>("named-object");

  const analysisMethod = getFromDataPath(dataPath, state, isAnalysisMethod);
  const operations: Operation[] = useMemo(() => analysisMethod?.operations ?? [], [analysisMethod]);
  const documentReferences: DocumentReference[] = useMemo(
    () => analysisMethod?.documentRefs ?? [],
    [analysisMethod],
  );
  const hasCodeTemplate = analysisMethod?.codeTemplate !== undefined;

  /**
   * Event handler to update the active tab.
   *
   * @param newTab - Tab key to activate.
   */
  const updateActiveTab = useEffectEvent((newTab: string) => {
    setActiveTab(newTab);
  });

  /**
   * Change the active operation tab if it no longer exists.
   */
  useEffect(() => {
    if (
      activeTab.startsWith("operation-") &&
      operations.length > 0 &&
      parseInt(activeTab.split("-")[1], 10) >= operations.length
    ) {
      updateActiveTab(`operation-${operations.length - 1}`);
    } else if (activeTab.startsWith("operation-") && operations.length === 0) {
      updateActiveTab("named-object");
    }
  }, [operations.length, activeTab]);

  /**
   * Handles adding a new operation to the method.
   */
  const handleAddOperation = (): void => {
    /* istanbul ignore else -- Disabled buttons cannot invoke this handler from the UI */
    if (!disabled && analysisMethod) {
      const operationCount = operations.length;
      const newOperation: Operation = {
        id: crypto.randomUUID(),
        name: `Operation_${operationCount + 1}`,
        label: `Operation ${operationCount + 1}`,
        description: "",
        order: operationCount,
        referencedOperationRelationships: [],
        resultPattern: "",
      };

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: `${dataPath}/operations/${operationCount}`,
        value: newOperation,
      });
    }
  };

  /**
   * Handles adding a new document reference to the method.
   */
  const handleAddDocumentReference = (): void => {
    /* istanbul ignore else -- Disabled buttons cannot invoke this handler from the UI */
    if (!disabled && analysisMethod) {
      const documentReferenceCount = documentReferences.length;
      const newDocumentReference: DocumentReference = {
        referenceDocumentId: "",
        pageRefs: [],
      };

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: `${dataPath}/documentRefs/${documentReferenceCount}`,
        value: newDocumentReference,
      });
    }
  };

  /**
   * Handles adding a new programming code template to the method.
   */
  const handleAddCodeTemplate = (): void => {
    /* istanbul ignore else -- Disabled buttons cannot invoke this handler from the UI */
    if (!disabled && analysisMethod && !hasCodeTemplate) {
      const newCodeTemplate: AnalysisProgrammingCodeTemplate = {
        context: "",
        code: "",
        parameters: [],
      };

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: `${dataPath}/codeTemplate`,
        value: newCodeTemplate,
      });
    }
  };

  const idRoute = dataPath.replace(/\//g, "-");

  return (
    <div className="border rounded p-3 mb-3 position-relative">
      {canRename && (
        <div className="position-absolute top-0 end-0 m-2">
          <RemoveArsItemButton
            dataPath={dataPath}
            disabled={disabled}
          />
        </div>
      )}

      <Tab.Container
        activeKey={activeTab}
        onSelect={(k) => {
          /* istanbul ignore else */
          if (k) {
            setActiveTab(k);
          }
        }}
      >
        <Nav
          variant="tabs"
          className="mb-3"
        >
          <Nav.Item>
            <Nav.Link eventKey="named-object">Properties</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="code-template">Code template</Nav.Link>
          </Nav.Item>
          {operations.map((operation, index) => (
            <Nav.Item key={operation.id}>
              <Nav.Link eventKey={`operation-${index}`}>{operation.name}</Nav.Link>
            </Nav.Item>
          ))}
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="named-object">
            <NamedObjectDisplay
              dataPath={dataPath}
              canRename={canRename}
              disabled={disabled}
            />

            {documentReferences.map((documentReference, index) => (
              <DocumentReferenceControl
                key={`${documentReference.referenceDocumentId}-${index}`}
                dataPath={`${dataPath}/documentRefs/${index}`}
                disabled={disabled}
              />
            ))}

            <AsyncButton
              id={`${idRoute}-add-document-reference`}
              onClick={handleAddDocumentReference}
              className="d-flex align-items-center btn-sm btn-success mb-3"
              disabled={disabled}
            >
              <span className="me-2">Add document reference</span>
              <PlusCircle />
            </AsyncButton>

            <AsyncButton
              id={`${idRoute}-add-operation`}
              onClick={handleAddOperation}
              className="d-flex align-items-center btn-sm btn-success"
              disabled={disabled}
            >
              <span className="me-2">Add operation</span>
              <PlusCircle />
            </AsyncButton>
          </Tab.Pane>

          <Tab.Pane eventKey="code-template">
            {hasCodeTemplate && (
              <AnalysisProgrammingCodeTemplateControl
                dataPath={`${dataPath}/codeTemplate`}
                disabled={disabled}
              />
            )}

            <AsyncButton
              id={`${idRoute}-add-code-template`}
              onClick={handleAddCodeTemplate}
              className="d-flex align-items-center btn-sm btn-success"
              disabled={disabled || hasCodeTemplate}
            >
              <span className="me-2">Add code template</span>
              <PlusCircle />
            </AsyncButton>
          </Tab.Pane>

          {operations.map((operation, index) => (
            <Tab.Pane
              key={operation.id}
              eventKey={`operation-${index}`}
            >
              <OperationControl
                dataPath={`${dataPath}/operations/${index}`}
                disabled={disabled}
                siblingCount={operations.length}
              />
            </Tab.Pane>
          ))}
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

AnalysisMethodControl.displayName = "AnalysisMethodControl";
